import { useState, useEffect, useCallback } from 'react';
import { AuctionType } from '../../types';

interface SavedSearch {
  id: string;
  user_id: string;
  name: string;
  search_term: string | null;
  auction_types: string[];
  tlds: string[];
  has_bids: boolean | null;
  no_dashes: boolean;
  no_numbers: boolean;
  min_price: number | null;
  max_price: number | null;
  min_length: number | null;
  max_length: number | null;
  sort_by: string;
  enabled: boolean;
  notify_email: string;
  created_at: string;
  last_run: string | null;
  last_run_results: number;
  run_count: number;
}

interface AutomationRun {
  id: string;
  search_id: string;
  search_name: string;
  timestamp: string;
  results_count: number;
  email_sent: boolean;
  error: string | null;
}

interface AutomationsProps {
  username: string;
  className?: string;
}

const API_BASE = import.meta.env.PROD
  ? 'https://zeropointdomains.com/api'
  : '/api';

const AVAILABLE_TLDS = ['com', 'net', 'org', 'io', 'co', 'ai', 'app', 'dev', 'xyz', 'info'];
const AUCTION_TYPES: AuctionType[] = ['Dropped', 'PrivateSeller', 'PreRelease'];

export function Automations({ username, className = '' }: AutomationsProps) {
  const [automations, setAutomations] = useState<SavedSearch[]>([]);
  const [history, setHistory] = useState<AutomationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [_selectedAutomation, _setSelectedAutomation] = useState<SavedSearch | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [runningTest, setRunningTest] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    searchTerm: '',
    notifyEmail: '',
    auctionTypes: ['Dropped', 'PrivateSeller'] as string[],
    tlds: ['com'] as string[],
    hasBids: null as boolean | null,
    noDashes: true,
    noNumbers: true,
    minPrice: null as number | null,
    maxPrice: null as number | null,
    minLength: null as number | null,
    maxLength: null as number | null,
    sortBy: 'EndTimeAsc'
  });

  const fetchAutomations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/automations?userId=${username}`);
      if (!res.ok) throw new Error('Failed to fetch automations');
      const data = await res.json();
      setAutomations(data.automations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [username]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/automations/history?limit=50`);
      if (!res.ok) throw new Error('Failed to fetch history');
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error('History fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchAutomations();
    fetchHistory();
  }, [fetchAutomations, fetchHistory]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_BASE}/automations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: username,
          ...formData
        })
      });

      if (!res.ok) throw new Error('Failed to create automation');

      setShowModal(false);
      setModalStep(1);
      resetForm();
      fetchAutomations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create automation');
    }
  };

  const handleToggle = async (automation: SavedSearch) => {
    try {
      const res = await fetch(`${API_BASE}/automations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: automation.id,
          enabled: !automation.enabled
        })
      });

      if (!res.ok) throw new Error('Failed to update automation');
      fetchAutomations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this automation?')) return;

    try {
      const res = await fetch(`${API_BASE}/automations?id=${id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete automation');
      fetchAutomations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleTestRun = async () => {
    try {
      setRunningTest(true);
      const res = await fetch(`${API_BASE}/automations/run`, {
        method: 'GET'
      });

      if (!res.ok) throw new Error('Test run failed');

      const data = await res.json();
      alert(`Test run complete! Processed ${data.processedCount} automations.`);
      fetchAutomations();
      fetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test run failed');
    } finally {
      setRunningTest(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      searchTerm: '',
      notifyEmail: '',
      auctionTypes: ['Dropped', 'PrivateSeller'],
      tlds: ['com'],
      hasBids: null,
      noDashes: true,
      noNumbers: true,
      minPrice: null,
      maxPrice: null,
      minLength: null,
      maxLength: null,
      sortBy: 'EndTimeAsc'
    });
  };

  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
  };

  return (
    <div className={`bg-black text-green-500 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-green-500">
        <div>
          <h1 className="text-2xl font-bold tracking-wider">AUTOMATIONS</h1>
          <p className="text-green-600 text-sm mt-1">
            Saved searches that run daily at 8:00 AM UTC and email results
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleTestRun}
            disabled={runningTest}
            className="px-4 py-2 border border-green-700 hover:bg-green-900 transition-colors text-sm disabled:opacity-50"
          >
            {runningTest ? 'RUNNING...' : 'TEST RUN NOW'}
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`px-4 py-2 border transition-colors text-sm ${
              showHistory ? 'bg-green-500 text-black border-green-500' : 'border-green-500 hover:bg-green-900'
            }`}
          >
            {showHistory ? 'SHOW AUTOMATIONS' : 'VIEW HISTORY'}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-green-500 text-black font-bold hover:bg-green-400 transition-colors"
          >
            + NEW AUTOMATION
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-red-500 bg-red-900/20 text-red-400">
          ERROR: {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-300 hover:text-white">[X]</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="text-green-500 animate-pulse">LOADING AUTOMATIONS...</div>
        </div>
      ) : showHistory ? (
        /* History View */
        <div className="space-y-3">
          <h2 className="text-lg font-bold mb-4 text-green-400">RUN HISTORY</h2>
          {history.length === 0 ? (
            <div className="text-center py-8 text-green-600">No automation runs yet.</div>
          ) : (
            <div className="border border-green-700">
              <table className="w-full">
                <thead className="bg-green-900/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold">TIMESTAMP</th>
                    <th className="px-4 py-3 text-left text-xs font-bold">SEARCH NAME</th>
                    <th className="px-4 py-3 text-left text-xs font-bold">RESULTS</th>
                    <th className="px-4 py-3 text-left text-xs font-bold">EMAIL</th>
                    <th className="px-4 py-3 text-left text-xs font-bold">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => (
                    <tr key={run.id} className="border-t border-green-900 hover:bg-green-900/20">
                      <td className="px-4 py-3 text-sm text-green-400">
                        {new Date(run.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm">{run.search_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={run.results_count > 0 ? 'text-green-400' : 'text-green-700'}>
                          {run.results_count} domains
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {run.email_sent ? (
                          <span className="text-green-400">✓ SENT</span>
                        ) : (
                          <span className="text-green-700">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {run.error ? (
                          <span className="text-red-400" title={run.error}>ERROR</span>
                        ) : (
                          <span className="text-green-400">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Automations List */
        <div className="space-y-4">
          {automations.length === 0 ? (
            <div className="text-center py-12 border border-green-700 border-dashed">
              <p className="text-green-600 mb-4">No automations configured yet.</p>
              <button
                onClick={() => setShowModal(true)}
                className="px-6 py-2 border border-green-500 hover:bg-green-900 transition-colors"
              >
                CREATE YOUR FIRST AUTOMATION
              </button>
            </div>
          ) : (
            automations.map((auto) => (
              <div
                key={auto.id}
                className={`border p-4 transition-all ${
                  auto.enabled ? 'border-green-500 bg-green-900/10' : 'border-green-800 bg-black'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold">{auto.name}</h3>
                      <span className={`px-2 py-0.5 text-xs ${
                        auto.enabled ? 'bg-green-500 text-black' : 'bg-green-900 text-green-500'
                      }`}>
                        {auto.enabled ? 'ACTIVE' : 'PAUSED'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-600 mb-3">
                      <div>
                        <span className="text-green-700">Search:</span>{' '}
                        <span className="text-green-400">{auto.search_term || 'All domains'}</span>
                      </div>
                      <div>
                        <span className="text-green-700">TLDs:</span>{' '}
                        <span className="text-green-400">{auto.tlds?.join(', ') || 'com'}</span>
                      </div>
                      <div>
                        <span className="text-green-700">Email:</span>{' '}
                        <span className="text-green-400">{auto.notify_email}</span>
                      </div>
                      <div>
                        <span className="text-green-700">Runs:</span>{' '}
                        <span className="text-green-400">{auto.run_count}</span>
                      </div>
                    </div>

                    <div className="text-xs text-green-700">
                      Last run: {auto.last_run ? new Date(auto.last_run).toLocaleString() : 'Never'} |{' '}
                      Last results: {auto.last_run_results} domains
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleToggle(auto)}
                      className={`px-3 py-1 text-sm border transition-colors ${
                        auto.enabled
                          ? 'border-yellow-500 text-yellow-500 hover:bg-yellow-900/30'
                          : 'border-green-500 text-green-500 hover:bg-green-900/30'
                      }`}
                    >
                      {auto.enabled ? 'PAUSE' : 'ENABLE'}
                    </button>
                    <button
                      onClick={() => handleDelete(auto.id)}
                      className="px-3 py-1 text-sm border border-red-500 text-red-500 hover:bg-red-900/30 transition-colors"
                    >
                      DELETE
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Multi-Step Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div
            className="bg-black border-2 border-green-500 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 30px rgba(34, 197, 94, 0.3)' }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-green-500">
              <div>
                <h2 className="text-xl font-bold">NEW AUTOMATION</h2>
                <p className="text-green-600 text-sm">Step {modalStep} of 3</p>
              </div>
              <button
                onClick={() => { setShowModal(false); setModalStep(1); resetForm(); }}
                className="text-green-500 hover:text-green-300 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Progress Bar */}
            <div className="flex border-b border-green-700">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`flex-1 py-2 text-center text-sm ${
                    step === modalStep
                      ? 'bg-green-500 text-black font-bold'
                      : step < modalStep
                      ? 'bg-green-900 text-green-400'
                      : 'bg-black text-green-700'
                  }`}
                >
                  {step === 1 ? 'BASICS' : step === 2 ? 'FILTERS' : 'REVIEW'}
                </div>
              ))}
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {modalStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">AUTOMATION NAME *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Premium Domains Daily"
                      className="w-full bg-black border border-green-500 px-4 py-2 text-green-400 focus:outline-none focus:border-green-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2">SEARCH KEYWORD</label>
                    <input
                      type="text"
                      value={formData.searchTerm}
                      onChange={(e) => setFormData({ ...formData, searchTerm: e.target.value })}
                      placeholder="e.g., law, legal, attorney (leave empty for all)"
                      className="w-full bg-black border border-green-500 px-4 py-2 text-green-400 focus:outline-none focus:border-green-300"
                    />
                    <p className="text-green-700 text-xs mt-1">Optional: Filter by keyword in domain name</p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2">NOTIFICATION EMAIL *</label>
                    <input
                      type="email"
                      value={formData.notifyEmail}
                      onChange={(e) => setFormData({ ...formData, notifyEmail: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full bg-black border border-green-500 px-4 py-2 text-green-400 focus:outline-none focus:border-green-300"
                    />
                    <p className="text-green-700 text-xs mt-1">Results will be emailed daily at 8:00 AM UTC</p>
                  </div>
                </div>
              )}

              {modalStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold mb-2">TLDs</label>
                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_TLDS.map((tld) => (
                        <button
                          key={tld}
                          onClick={() => setFormData({
                            ...formData,
                            tlds: toggleArrayItem(formData.tlds, tld)
                          })}
                          className={`px-3 py-1 text-sm border transition-colors ${
                            formData.tlds.includes(tld)
                              ? 'bg-green-500 text-black border-green-500'
                              : 'border-green-700 hover:border-green-500'
                          }`}
                        >
                          .{tld}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2">AUCTION TYPES</label>
                    <div className="flex flex-wrap gap-2">
                      {AUCTION_TYPES.map((type) => (
                        <button
                          key={type}
                          onClick={() => setFormData({
                            ...formData,
                            auctionTypes: toggleArrayItem(formData.auctionTypes, type)
                          })}
                          className={`px-3 py-1 text-sm border transition-colors ${
                            formData.auctionTypes.includes(type)
                              ? 'bg-green-500 text-black border-green-500'
                              : 'border-green-700 hover:border-green-500'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold mb-2">MIN PRICE ($)</label>
                      <input
                        type="number"
                        value={formData.minPrice || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          minPrice: e.target.value ? Number(e.target.value) : null
                        })}
                        placeholder="No min"
                        className="w-full bg-black border border-green-700 px-4 py-2 text-green-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2">MAX PRICE ($)</label>
                      <input
                        type="number"
                        value={formData.maxPrice || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          maxPrice: e.target.value ? Number(e.target.value) : null
                        })}
                        placeholder="No max"
                        className="w-full bg-black border border-green-700 px-4 py-2 text-green-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold mb-2">MIN LENGTH</label>
                      <input
                        type="number"
                        value={formData.minLength || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          minLength: e.target.value ? Number(e.target.value) : null
                        })}
                        placeholder="No min"
                        className="w-full bg-black border border-green-700 px-4 py-2 text-green-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2">MAX LENGTH</label>
                      <input
                        type="number"
                        value={formData.maxLength || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          maxLength: e.target.value ? Number(e.target.value) : null
                        })}
                        placeholder="No max"
                        className="w-full bg-black border border-green-700 px-4 py-2 text-green-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.noDashes}
                        onChange={(e) => setFormData({ ...formData, noDashes: e.target.checked })}
                        className="w-4 h-4 accent-green-500"
                      />
                      <span className="text-sm">No Dashes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.noNumbers}
                        onChange={(e) => setFormData({ ...formData, noNumbers: e.target.checked })}
                        className="w-4 h-4 accent-green-500"
                      />
                      <span className="text-sm">No Numbers</span>
                    </label>
                  </div>
                </div>
              )}

              {modalStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-green-400 mb-4">REVIEW YOUR AUTOMATION</h3>

                  <div className="border border-green-700 p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-green-700">Name:</span>
                      <span className="text-green-400 font-bold">{formData.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Search Term:</span>
                      <span className="text-green-400">{formData.searchTerm || 'All domains'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Email:</span>
                      <span className="text-green-400">{formData.notifyEmail}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">TLDs:</span>
                      <span className="text-green-400">{formData.tlds.map(t => `.${t}`).join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Auction Types:</span>
                      <span className="text-green-400">{formData.auctionTypes.join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Price Range:</span>
                      <span className="text-green-400">
                        ${formData.minPrice || 0} - ${formData.maxPrice || '∞'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Length:</span>
                      <span className="text-green-400">
                        {formData.minLength || 1} - {formData.maxLength || '∞'} chars
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Filters:</span>
                      <span className="text-green-400">
                        {[
                          formData.noDashes && 'No dashes',
                          formData.noNumbers && 'No numbers'
                        ].filter(Boolean).join(', ') || 'None'}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-green-900/20 border border-green-700 text-sm">
                    <p className="text-green-400">
                      ✓ This automation will run daily at 8:00 AM UTC
                    </p>
                    <p className="text-green-400">
                      ✓ Results will be emailed to {formData.notifyEmail}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between px-6 py-4 border-t border-green-700">
              <button
                onClick={() => {
                  if (modalStep === 1) {
                    setShowModal(false);
                    resetForm();
                  } else {
                    setModalStep(modalStep - 1);
                  }
                }}
                className="px-4 py-2 border border-green-700 hover:border-green-500 transition-colors"
              >
                {modalStep === 1 ? 'CANCEL' : 'BACK'}
              </button>
              <button
                onClick={() => {
                  if (modalStep < 3) {
                    setModalStep(modalStep + 1);
                  } else {
                    handleCreate();
                  }
                }}
                disabled={
                  (modalStep === 1 && (!formData.name || !formData.notifyEmail)) ||
                  (modalStep === 2 && formData.tlds.length === 0)
                }
                className="px-6 py-2 bg-green-500 text-black font-bold hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modalStep < 3 ? 'NEXT' : 'CREATE AUTOMATION'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
